import i18n from "../../i18n";
const API_URL = process.env.REACT_APP_PAYMENT_SERVICE_BASE_URL || '';

export interface StripeAccountStatus {
    businessId: string;
    stripeAccountId: string | null;
    connected: boolean;
    chargesEnabled: boolean;
    payoutsEnabled: boolean;
    detailsSubmitted: boolean;
    country: string | null;
}

async function gqlPost<T>(token: string, query: string, variables?: object): Promise<T> {
    const res = await fetch(API_URL, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`, "Accept-Language": i18n.language || "pt",
        },
        body: JSON.stringify({ query, variables }),
    });
    const json = await res.json();
    if (json.errors) {
        const msg = json.errors.map((e: { message: string }) => e.message).join(', ');
        throw new Error(msg);
    }
    return json.data as T;
}

const STATUS_FIELDS = `
    businessId
    stripeAccountId
    connected
    chargesEnabled
    payoutsEnabled
    detailsSubmitted
    country
`;

/* ---- Consumer GLOBAL card (read-only view in the client cadastro) ---- */

export interface ConsumerCardView {
    hasCard: boolean;
    brand?: string | null;
    last4?: string | null;
    expMonth?: number | null;
    expYear?: number | null;
}

/** Read-only: the client's global card on file (by email). The card is the
 *  consumer's — managed in their own profile; the admin only sees the status. */
export async function getConsumerCardByEmail(
    businessId: number | string,
    email: string,
    token: string
): Promise<ConsumerCardView> {
    const data = await gqlPost<{ consumerCardByEmail: ConsumerCardView | null }>(
        token,
        `query($b: ID!, $e: String!) {
            consumerCardByEmail(businessId: $b, email: $e) { hasCard brand last4 expMonth expYear }
        }`,
        { b: businessId, e: email }
    );
    return data.consumerCardByEmail ?? { hasCard: false };
}

/* ---- Client card on file (Stripe SetupIntent + Elements) — legacy per-business; kept for compat ---- */

export interface ClientCardSetupIntent {
    clientSecret: string;
    publishableKey: string;
    stripeAccountId: string;
    customerId: string;
}

/** Start saving a card for a client — returns what the frontend needs to mount
 *  Stripe.js Elements and confirm the SetupIntent. Gated by CLIENTS (edit). */
export async function createClientCardSetupIntent(
    businessId: number | string,
    clientId: number | string,
    token: string
): Promise<ClientCardSetupIntent> {
    const data = await gqlPost<{ createClientCardSetupIntent: ClientCardSetupIntent }>(
        token,
        `mutation($b: ID!, $c: ID!) {
            createClientCardSetupIntent(businessId: $b, clientId: $c) {
                clientSecret publishableKey stripeAccountId customerId
            }
        }`,
        { b: businessId, c: clientId }
    );
    return data.createClientCardSetupIntent;
}

/** Persist the card after the client confirms the SetupIntent client-side. */
export async function saveClientCard(
    businessId: number | string,
    clientId: number | string,
    setupIntentId: string,
    token: string
): Promise<boolean> {
    const data = await gqlPost<{ saveClientCard: boolean }>(
        token,
        `mutation($b: ID!, $c: ID!, $s: String!) {
            saveClientCard(businessId: $b, clientId: $c, setupIntentId: $s)
        }`,
        { b: businessId, c: clientId, s: setupIntentId }
    );
    return !!data.saveClientCard;
}

/** Detach the saved card at Stripe and clear it on the client. */
export async function removeClientCard(
    businessId: number | string,
    clientId: number | string,
    token: string
): Promise<boolean> {
    const data = await gqlPost<{ removeClientCard: boolean }>(
        token,
        `mutation($b: ID!, $c: ID!) { removeClientCard(businessId: $b, clientId: $c) }`,
        { b: businessId, c: clientId }
    );
    return !!data.removeClientCard;
}

/* ---- Pre-authorization hold settlement (attendant, at the payment screen) ---- */

export interface ClientCardHold {
    paymentIntentId: string;
    status: string;
    amount?: number | null;
}

/** Capture the hold — full (omit amount) or partial (no-show). Returns the captured amount. */
export async function captureClientHold(
    businessId: number | string,
    paymentIntentId: string,
    amount: number | null,
    token: string
): Promise<ClientCardHold> {
    const data = await gqlPost<{ captureClientHold: ClientCardHold }>(
        token,
        `mutation($b: ID!, $p: String!, $a: Float) {
            captureClientHold(businessId: $b, paymentIntentId: $p, amount: $a) { paymentIntentId status amount }
        }`,
        { b: businessId, p: paymentIntentId, a: amount }
    );
    return data.captureClientHold;
}

/** Charge an additional amount on the saved card (remainder beyond a partial hold). */
export async function chargeClientCard(
    businessId: number | string,
    clientId: number | string,
    amount: number,
    currency: string,
    token: string
): Promise<ClientCardHold> {
    const data = await gqlPost<{ chargeClientCard: ClientCardHold }>(
        token,
        `mutation($b: ID!, $c: ID!, $a: Float!, $cur: String) {
            chargeClientCard(businessId: $b, clientId: $c, amount: $a, currency: $cur) { paymentIntentId status amount }
        }`,
        { b: businessId, c: clientId, a: amount, cur: currency }
    );
    return data.chargeClientCard;
}

/** Cancel/release the hold (attendant receives another way). */
export async function cancelClientHold(
    businessId: number | string,
    paymentIntentId: string,
    token: string
): Promise<boolean> {
    const data = await gqlPost<{ cancelClientHold: boolean }>(
        token,
        `mutation($b: ID!, $p: String!) { cancelClientHold(businessId: $b, paymentIntentId: $p) }`,
        { b: businessId, p: paymentIntentId }
    );
    return !!data.cancelClientHold;
}

export async function getStripeAccountStatus(
    businessId: string,
    token: string
): Promise<StripeAccountStatus> {
    const data = await gqlPost<{ getStripeAccountStatus: StripeAccountStatus }>(
        token,
        `query getStripeAccountStatus($businessId: ID!) {
            getStripeAccountStatus(businessId: $businessId) { ${STATUS_FIELDS} }
        }`,
        { businessId }
    );
    return data.getStripeAccountStatus;
}

export async function refreshStripeAccountStatus(
    businessId: string,
    token: string
): Promise<StripeAccountStatus> {
    const data = await gqlPost<{ refreshStripeAccountStatus: StripeAccountStatus }>(
        token,
        `mutation refreshStripeAccountStatus($businessId: ID!) {
            refreshStripeAccountStatus(businessId: $businessId) { ${STATUS_FIELDS} }
        }`,
        { businessId }
    );
    return data.refreshStripeAccountStatus;
}

export async function createStripeDashboardLink(
    businessId: string,
    token: string
): Promise<string> {
    const data = await gqlPost<{ createStripeDashboardLink: string }>(
        token,
        `mutation createStripeDashboardLink($businessId: ID!) {
            createStripeDashboardLink(businessId: $businessId)
        }`,
        { businessId }
    );
    return data.createStripeDashboardLink;
}

export async function createStripeOnboardingLink(
    businessId: string,
    country: string,
    returnUrl: string,
    refreshUrl: string,
    token: string
): Promise<string> {
    const data = await gqlPost<{ createStripeOnboardingLink: string }>(
        token,
        `mutation createStripeOnboardingLink(
            $businessId: ID!
            $country: String!
            $returnUrl: String!
            $refreshUrl: String!
        ) {
            createStripeOnboardingLink(
                businessId: $businessId
                country: $country
                returnUrl: $returnUrl
                refreshUrl: $refreshUrl
            )
        }`,
        { businessId, country, returnUrl, refreshUrl }
    );
    return data.createStripeOnboardingLink;
}
